
import * as React from 'react';
import { makeTestId } from '../../utils/Declarative';
import { getSliderTickBarStyles } from './SliderStyles';

export interface SliderTickBarProps {
  /**
   * The number of ticks to display.  The ticks will be evenly spaced out on the bar, with the first being to the far
   * left, and the last being to the far right.  If there is only 1 tick, that will be in the center.
   */
  tickCount: number;

  /**
   * Test id to be attached to the slider and its contents.
   * The suffix '-SliderTickBar' will be automatically added
   */
  testId: string;
}

export function calculateTickProportionalPositions(tickCount: number): number[] {
  const tickPositionsAsProportions = [];
  if (tickCount === 1) {
    tickPositionsAsProportions.push(0.5);
  } else if (tickCount > 1) {
    // We subtract 1 from tickCount to make the final division below result in exactly 1.
    // For example, if tickCount === 4:
    // * (dividing by tickCount):     [0, 0.25, 0.5,  0.75]
    // * (dividing by tickCount - 1): [0, 0.33, 0.66, 1.0]
    // We want to position the marks like the later.
    const tickCountForSpacing = tickCount - 1;
    for (let i = 0; i < tickCount; i++) {
      tickPositionsAsProportions.push(i / tickCountForSpacing);
    }
  }
  return tickPositionsAsProportions;
}

/**
 * The tick bar is a bar, of ticks.  It is not interactive.
 */
export function SliderTickBarWidget(props: SliderTickBarProps): JSX.Element {
  const tickPositionsAsProportions = calculateTickProportionalPositions(props.tickCount);
  const tickPositionsAsPercentages = tickPositionsAsProportions.map(x => x * 100);
  const styles = getSliderTickBarStyles();

  return (
    <div style={styles.Bar} data-tb-test-id={makeTestId(props.testId, 'SliderTickBar')}>
      {tickPositionsAsPercentages.map((tickPositionAsPercentage, i) =>
        <span key={i} style={{ ...styles.Tick, left: `${tickPositionAsPercentage}%` }} />
      )}
    </div>
  );
}
